/**
 * Single registry of every browser-storage key the app uses. Each key is tagged
 * with a {@link STORAGE_SCOPE}:
 *
 * - `SESSION` — belongs to the logged-in account (auth tokens, the telnyx WebRTC
 *   token). Wiped at every session boundary by `clearSessionStorage()` so the
 *   next login never inherits the previous account's data.
 * - `DEVICE` — per-device UI preferences (table layout, sidebar, theme). These
 *   intentionally survive logout; clearing them would just reset the user's
 *   customizations on every sign-out.
 *
 * Add new persisted keys HERE rather than hardcoding a string at the call site,
 * then read `.key` from this registry. A new `SESSION` key is then cleared on
 * logout automatically — no need to touch the logout flow.
 *
 * Key strings must stay byte-identical to what shipped previously, otherwise a
 * deploy silently orphans every user's stored value under the old name.
 */
export const STORAGE_SCOPE = {
  SESSION: 'session',
  DEVICE: 'device',
} as const

type StorageScope = (typeof STORAGE_SCOPE)[keyof typeof STORAGE_SCOPE]

interface StorageKeyDef {
  key: string
  scope: StorageScope
}

export const STORAGE_KEYS = {
  /** Zustand-persisted auth state: access/refresh tokens, user, permissions. */
  auth: { key: 'cargo-auth', scope: STORAGE_SCOPE.SESSION },
  /**
   * OTP "remember this device" token from the verify endpoint. DEVICE-scoped on
   * purpose: it must survive logout (unlike the auth blob) so the next login on
   * this browser skips the OTP challenge. Sent with the login request.
   */
  deviceToken: { key: 'cargo-device-token', scope: STORAGE_SCOPE.DEVICE },
  /** Telnyx WebRTC login token. Non-user-scoped key — MUST be session-cleared. */
  telnyxToken: { key: 'telephony.webrtc-token', scope: STORAGE_SCOPE.SESSION },
  tableDensity: { key: 'cargo-table-density', scope: STORAGE_SCOPE.DEVICE },
  tableAppearance: {
    key: 'cargo-table-appearance',
    scope: STORAGE_SCOPE.DEVICE,
  },
  sidePanel: { key: 'cargo-side-panel', scope: STORAGE_SCOPE.DEVICE },
  manageFields: { key: 'cargo-manage-fields-v2', scope: STORAGE_SCOPE.DEVICE },
  /** App-wide appearance (light / dark / midnight). DEVICE-scoped so the choice
   *  survives logout. NOTE: `index.html` has a pre-paint bootstrap that reads
   *  this exact key string — keep them in sync if it ever changes. */
  appTheme: { key: 'cargo-app-theme', scope: STORAGE_SCOPE.DEVICE },
  sidebarCollapsed: { key: 'sidebar:collapsed', scope: STORAGE_SCOPE.DEVICE },
  devSample: { key: 'order-form:dev-sample', scope: STORAGE_SCOPE.DEVICE },
  /**
   * First-run onboarding "seen" step ids (JSON string array). DEVICE-scoped as a
   * stop-gap until the backend exposes per-user UI state — see
   * `#/shared/lib/onboarding`, the single place to swap this for a server
   * round-trip without touching any call site.
   */
  onboardingSeen: { key: 'cargo-onboarding-seen', scope: STORAGE_SCOPE.DEVICE },
  /** Recently-inserted template field ids (JSON string array, most-recent-first)
   *  surfaced atop the editor's `/` menu. */
  templateRecentFields: {
    key: 'cargo-template-recent-fields',
    scope: STORAGE_SCOPE.DEVICE,
  },
  /**
   * Smart-compose local word statistics (per-agent n-gram counts, no message
   * bodies). The stored key is suffixed with the user id
   * (`cargo-smart-compose:<userId>`) like the per-table pin keys below, so the
   * DEVICE scope is safe: each account only ever reads its own model, and the
   * learned style survives logout. Size is capped in the model itself
   * (~5k pairs ≈ 150 KB).
   */
  smartCompose: { key: 'cargo-smart-compose', scope: STORAGE_SCOPE.DEVICE },
  /** Open workspace tabs (in-app tab strip). SESSION-scoped: tab hrefs point at
   *  account data (order/lead ids), so the next login starts with a clean set. */
  workspaceTabs: { key: 'cargo-workspace-tabs', scope: STORAGE_SCOPE.SESSION },
  /** Whether the workspace tab strip is pinned open at the top (vs. the default
   *  hover-only flyout). Pure UI preference, so it survives logout. */
  workspaceTabsPinned: {
    key: 'cargo-workspace-tabs-pinned',
    scope: STORAGE_SCOPE.DEVICE,
  },
} as const satisfies Record<string, StorageKeyDef>

// Data-table column-pin overrides use one key per table (e.g. `leads-table-pins`,
// supplied by each table's `storageKey` prop). They're device-scoped (never
// session-cleared), so they're not enumerated in STORAGE_KEYS above.
